import type { InspectionOrgUser } from "@/lib/inspection-portal-api";

/** Lightweight email check aligned with invite form expectations. */
export function isValidAssignEmail(value: string): boolean {
  const email = value.trim();
  if (!email) return false;
  // Require a single @ with non-empty local and domain parts, and a TLD.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Derive a display name from the email local-part (e.g. jane.doe → Jane Doe). */
export function deriveNameFromEmail(email: string): string {
  const local = email.trim().split("@")[0] ?? "";
  if (!local) return "";
  return local
    .split(/[._+\-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function findOrgUserByEmail(
  users: InspectionOrgUser[],
  email: string,
): InspectionOrgUser | undefined {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return undefined;
  return users.find((u) => u.email.toLowerCase() === normalized);
}

export function formatOrgUserLabel(user: InspectionOrgUser): string {
  return user.name ? `${user.email} (${user.name})` : user.email;
}

export function filterOrgUsers(
  users: InspectionOrgUser[],
  query: string,
): InspectionOrgUser[] {
  const q = query.trim().toLowerCase();
  if (!q) return users;
  return users.filter(
    (u) =>
      u.email.toLowerCase().includes(q) ||
      (u.name?.toLowerCase().includes(q) ?? false),
  );
}

export type AssignToValue =
  | { type: "user"; id: string; email: string; name: string | null }
  | { type: "invite"; email: string; name: string };

export function assignToDisplayLabel(value: AssignToValue | null): string {
  if (!value) return "";
  if (value.type === "invite") {
    return value.name ? `${value.email} (${value.name})` : value.email;
  }
  return value.name ? `${value.email} (${value.name})` : value.email;
}
